import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { FaStore, FaCity, FaEdit, FaList, FaPencilAlt, FaSearch, FaChevronDown, FaTrash, FaPlus, FaExclamationTriangle, FaRegFileAlt, FaArrowLeft } from 'react-icons/fa';
import { IoMdCart } from 'react-icons/io';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useCurrency } from '../../context/CurrencyContext';
import './Placeorders.css';

const API_BASE_URL = 'https://super-backend.hira-tech.co.uk';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// ✅ Local date helper — no timezone shift
const getLocalDateString = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const freshOrderDetails = () => ({
  orderTrackingId: '',
  orderStatus: 'Pending',
  orderPlacementDate: getLocalDateString(),
  orderDeliveryDate: '',
  orderDeliveryTime: '12:00:00',
  customer: '',
  phoneNumber: '',
  address: ''
});

const EMPTY_PRODUCT = {
  selectType: '',
  selectCategory: '',
  selectVariations: '',
  selectImplantCompany: '',
  selectImplantConnection: '',
  productSearch: '',
  quantity: 1,
  productPrice: '',
  total: 0,
  productId: null,
  availableStock: null,
  cityStock: [],
  selectedCityStock: null
};

// =====================================================================
// COURIER SERVICES (default + user-added, saved in this browser)
// =====================================================================
const DEFAULT_COURIER_SERVICES = [
  'Daewoo Thokar',
  'Daewoo Kalma Chowk',
  'Daewoo Faisalabad',
  'Daewoo Valencia Town',
  'TCS',
  'Bykea',
  '2D Car Service'
];
const COURIER_STORAGE_KEY = 'customCourierServices';

const loadSavedCouriers = () => {
  try {
    const raw = localStorage.getItem(COURIER_STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((s) => typeof s === 'string' && s.trim()) : [];
  } catch (e) {
    return [];
  }
};

const extractServerErrorMessage = (error) => {
  const fallback = 'An error occurred while placing the order. Please try again.';
  const data = error?.response?.data;

  if (!data) {
    return error?.message || fallback;
  }

  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (trimmed.startsWith('<')) {
      const match = trimmed.match(/<pre class="exception_value">([\s\S]*?)<\/pre>/);
      if (match) {
        const text = match[1]
          .replace(/<[^>]*>/g, '')
          .replace(/&quot;/g, '"')
          .replace(/&#x27;/g, "'")
          .replace(/&#39;/g, "'")
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&amp;/g, '&')
          .trim();
        if (text) return `Server error: ${text}`;
      }
      return 'Server error while saving the order. Please try again or contact support.';
    }
    return trimmed || fallback;
  }

  if (data.errors) {
    const errors = data.errors;
    let errorList = 'Validation errors:\n';
    Object.keys(errors).forEach((field) => {
      if (Array.isArray(errors[field])) errorList += `\n${field}: ${errors[field].join(', ')}`;
      else errorList += `\n${field}: ${errors[field]}`;
    });
    return errorList;
  }
  if (data.message) return data.message;
  if (data.error) return data.error;
  if (data.detail) return data.detail;

  try {
    const parts = Object.entries(data).map(([key, value]) =>
      `${key}: ${Array.isArray(value) ? value.join(', ') : typeof value === 'object' ? JSON.stringify(value) : value}`
    );
    if (parts.length > 0) return parts.join('\n');
  } catch (e) {}
  return fallback;
};

// =====================================================================
// PRODUCT FILTER HELPERS
// =====================================================================
const warnedFilters = new Set();
const norm = (s) => String(s ?? '').trim().toLowerCase();

const refParts = (value, idKeys, nameKeys) => {
  if (value && typeof value === 'object') {
    let id = null;
    let name = '';
    for (const k of idKeys) {
      if (value[k] !== undefined && value[k] !== null) { id = value[k]; break; }
    }
    for (const k of nameKeys) {
      if (value[k]) { name = String(value[k]); break; }
    }
    return { id, name };
  }
  if (value === null || value === undefined || value === '') return { id: null, name: '' };
  if (typeof value === 'number' || /^\d+$/.test(String(value))) return { id: value, name: '' };
  return { id: null, name: String(value) };
};

const FILTER_SPECS = {
  type: {
    keys: ['type', 'product_type', 'type_id'],
    idKeys: ['type_id', 'id'],
    nameKeys: ['type_name', 'name'],
    flatNameKeys: ['type_name'],
  },
  category: {
    keys: ['product_category', 'category', 'category_id'],
    idKeys: ['catrgort_id', 'category_id', 'id'],
    nameKeys: ['category_Name', 'category_name', 'name'],
    flatNameKeys: ['category_Name', 'category_name'],
  },
  variation: {
    keys: ['product_variation', 'variation', 'variation_id'],
    idKeys: ['variation_id', 'id'],
    nameKeys: ['variation_name', 'name'],
    flatNameKeys: ['variation_name'],
  },
  company: {
    keys: ['implant_company', 'implant_Company', 'company'],
    idKeys: ['company_id', 'id'],
    nameKeys: ['company_name', 'name'],
    flatNameKeys: ['implant_company_name', 'company_name', 'implant_Company'],
    fallback: (p) => {
      const b = p.product_brand || p.brand;
      return b && typeof b === 'object' ? (b.implant_company ?? b.implant_Company) : undefined;
    },
  },
  brand: {
    keys: ['product_brand', 'brand', 'brand_id'],
    idKeys: ['brand_id', 'id'],
    nameKeys: ['brand_name', 'name'],
    flatNameKeys: ['brand_name', 'brand_Name'],
  },
};

const productMatchesFilter = (product, filter, specName) => {
  if (!filter) return true;
  const spec = FILTER_SPECS[specName];

  let value;
  let found = false;
  for (const k of spec.keys) {
    if (k in product) { value = product[k]; found = true; break; }
  }
  if (!found && spec.fallback) {
    const fb = spec.fallback(product);
    if (fb !== undefined) { value = fb; found = true; }
  }
  if (!found) {
    for (const k of spec.flatNameKeys || []) {
      if (k in product) { found = true; break; }
    }
  }

  if (!found) {
    if (!warnedFilters.has(specName)) {
      warnedFilters.add(specName);
      console.warn(`[Place Order] Product data me "${specName}" ki info nahi hai, is filter ko client-side check nahi kiya ja sakta.`);
    }
    return true;
  }

  const hasId = filter.id !== undefined && filter.id !== null && filter.id !== '';
  const text = norm(filter.name);

  const matchOne = (val) => {
    const parts = refParts(val, spec.idKeys, spec.nameKeys);
    let name = parts.name;
    if (!name) {
      for (const k of spec.flatNameKeys || []) {
        if (product[k]) { name = String(product[k]); break; }
      }
    }

    if (hasId) {
      if (parts.id !== null && parts.id !== undefined) return String(parts.id) === String(filter.id);
      if (name && filter.name) return norm(name) === text;
      return false;
    }

    if (!text) return true;
    if (!name && parts.id !== null && parts.id !== undefined) return true;
    return norm(name).includes(text);
  };

  // Product ki variation/category kabhi list (many-to-many) bhi hoti hai
  if (Array.isArray(value)) return value.some(matchOne);
  return matchOne(value);
};

const filterProductsClientSide = (list, filters) =>
  list.filter((p) =>
    Object.keys(filters).every((specName) => productMatchesFilter(p, filters[specName], specName))
  );

// Brand (Implant Connection) kis Implant Company ka hai
const getBrandCompanyId = (brand) => {
  const c = brand?.implant_company ?? brand?.implant_Company ?? brand?.company ?? null;
  if (c && typeof c === 'object') return c.company_id ?? c.id ?? null;
  if (c !== null && c !== undefined && c !== '' && (typeof c === 'number' || /^\d+$/.test(String(c)))) return c;
  return brand?.company_id ?? brand?.implant_company_id ?? null;
};

const getBrandCompanyName = (brand) => {
  const c = brand?.implant_company ?? brand?.implant_Company ?? brand?.company ?? null;
  if (c && typeof c === 'object') return c.company_name ?? c.name ?? '';
  if (typeof c === 'string' && !/^\d+$/.test(c)) return c;
  return brand?.company_name ?? brand?.implant_company_name ?? '';
};

const filterBrandsByCompany = (brands, selectedCompanyId, selectedCompanyName, customCompanyText) => {
  const hasCompanyInfo = brands.some((b) => getBrandCompanyId(b) != null || getBrandCompanyName(b));
  if (!hasCompanyInfo) return brands;

  if (selectedCompanyId) {
    return brands.filter((b) => {
      const id = getBrandCompanyId(b);
      if (id != null) return String(id) === String(selectedCompanyId);
      const nm = getBrandCompanyName(b);
      return !!nm && !!selectedCompanyName && nm.toLowerCase() === String(selectedCompanyName).toLowerCase();
    });
  }

  const txt = String(customCompanyText || '').trim().toLowerCase();
  if (txt) {
    return brands.filter((b) => getBrandCompanyName(b).toLowerCase().includes(txt));
  }
  return brands;
};

// ✅ Variation kis Category ki hai (Variations page me category link hoti hai)
const getVariationCategoryId = (variation) => {
  const c = variation?.category ?? variation?.product_category ?? null;
  if (c && typeof c === 'object') return c.catrgort_id ?? c.category_id ?? c.id ?? null;
  if (c !== null && c !== undefined && c !== '' && (typeof c === 'number' || /^\d+$/.test(String(c)))) return c;
  return variation?.category_id ?? variation?.catrgort_id ?? null;
};

const getVariationCategoryName = (variation) => {
  const c = variation?.category ?? variation?.product_category ?? null;
  if (c && typeof c === 'object') return c.category_Name ?? c.category_name ?? c.name ?? '';
  if (typeof c === 'string' && !/^\d+$/.test(c)) return c;
  return variation?.category_name ?? variation?.category_Name ?? '';
};

// Selected category ke hisaab se sirf usi ki variations
const filterVariationsByCategory = (variations, selectedCategoryId, selectedCategoryName, customCategoryText) => {
  const hasCategoryInfo = variations.some((v) => getVariationCategoryId(v) != null || getVariationCategoryName(v));
  if (!hasCategoryInfo) return variations;

  if (selectedCategoryId) {
    return variations.filter((v) => {
      const id = getVariationCategoryId(v);
      if (id != null) return String(id) === String(selectedCategoryId);
      const nm = getVariationCategoryName(v);
      return !!nm && !!selectedCategoryName && nm.toLowerCase() === String(selectedCategoryName).toLowerCase();
    });
  }

  const txt = String(customCategoryText || '').trim().toLowerCase();
  if (txt) {
    return variations.filter((v) => getVariationCategoryName(v).toLowerCase().includes(txt));
  }
  return variations;
};

const getAuthToken = () => {
  const token = localStorage.getItem('access_token') ||
                localStorage.getItem('token') ||
                localStorage.getItem('accessToken');
  if (token) return token;
  const possibleKeys = ['token', 'authToken', 'access_token', 'accessToken', 'jwt'];
  for (let key of possibleKeys) {
    const storedData = localStorage.getItem(key);
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (parsed.access || parsed.token || parsed.access_token) {
          return parsed.access || parsed.token || parsed.access_token;
        }
      } catch (e) {
        return storedData;
      }
    }
  }
  return null;
};

const getLoggedInUser = () => {
  const possibleKeys = ['user', 'userInfo', 'user_data', 'authUser', 'loggedInUser'];
  for (const key of possibleKeys) {
    const stored = localStorage.getItem(key);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed && (parsed.first_Name || parsed.email || parsed.id)) {
          return parsed;
        }
      } catch (e) {}
    }
  }
  try {
    const tokenRaw = localStorage.getItem('token');
    if (tokenRaw) {
      const parsed = JSON.parse(tokenRaw);
      if (parsed && parsed.user) {
        return parsed.user;
      }
    }
  } catch (e) {}
  return null;
};

api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      if (typeof error.response.data === 'string' && error.response.data.trim().startsWith('<')) {
        console.error('Response data: [HTML error page]', error.response.status);
      } else {
        console.error('Response data:', error.response.data);
      }
      if (error.response.data && error.response.data.errors) {
        const errors = error.response.data.errors;
        let errorMessage = 'Validation errors:\n';
        Object.keys(errors).forEach(field => {
          if (Array.isArray(errors[field])) {
            errorMessage += `\n${field}: ${errors[field].join(', ')}`;
          } else {
            errorMessage += `\n${field}: ${errors[field]}`;
          }
        });
        error.customMessage = errorMessage;
      }
    }
    return Promise.reject(error);
  }
);

// ✅ Products API paginated ho to saare pages la kar aata hai
// (warna sirf pehla page milta tha aur client-side filter me product gayab ho jata tha)
const fetchAllProductPages = async (params) => {
  let url = `/api/product/products/?${params.toString()}`;
  let all = [];
  for (let page = 0; page < 30 && url; page++) {
    const res = await api.get(url);
    const data = res.data;
    if (Array.isArray(data)) return data; // paginated nahi hai
    const chunk = data?.results || data?.products || data?.data || [];
    all = all.concat(chunk);
    url = data?.next || null;
  }
  return all;
};

const Placeorders = () => {
  const location = useLocation();
  const searchContainerRef = useRef(null);
  const searchRequestRef = useRef(0);

  const {
    formatPrice,
    formatPriceFromCode,
    formatInShopCurrency,
    currency,
    shopBaseCurrency,
    shopBaseSymbol,
    shopCurrencySymbol,
    getCurrencySymbol,
    getCurrentCurrency,
    getCurrentCurrencySymbol,
  } = useCurrency();

  const displayCurrencyCode =
    (typeof getCurrentCurrency === 'function' && getCurrentCurrency()) || shopBaseCurrency || currency || '';
  const displayCurrencySymbol =
    (typeof getCurrentCurrencySymbol === 'function' && getCurrentCurrencySymbol()) ||
    shopCurrencySymbol ||
    shopBaseSymbol ||
    (typeof getCurrencySymbol === 'function' && getCurrencySymbol(displayCurrencyCode)) ||
    '';

  const formatMoney = (amount) => {
    const n = Number(amount || 0);
    try {
      if (typeof formatPriceFromCode === 'function') return formatPriceFromCode(n, displayCurrencyCode);
      if (typeof formatInShopCurrency === 'function') return formatInShopCurrency(n);
      if (typeof formatPrice === 'function') return formatPrice(n);
    } catch (e) {}
    return `${displayCurrencySymbol}${n.toFixed(2)}`;
  };

  const [types, setTypes] = useState([]);
  const [variations, setVariations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [implantCompanies, setImplantCompanies] = useState([]);
  const [brands, setBrands] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [shops, setShops] = useState([]);

  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState('all');

  const [submitting, setSubmitting] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const [selectedShopId, setSelectedShopId] = useState('');
  const [selectedShopName, setSelectedShopName] = useState('');
  const [shopsLoaded, setShopsLoaded] = useState(false);

  const [orderDetails, setOrderDetails] = useState(freshOrderDetails());

  const [addressMode, setAddressMode] = useState('select');
  const [manualAddress, setManualAddress] = useState('');

  const [orderedProducts, setOrderedProducts] = useState([]);

  // ✅ Jo table row abhi quantity/city se linked hai
  const [activeRowId, setActiveRowId] = useState(null);

  const [currentProduct, setCurrentProduct] = useState(EMPTY_PRODUCT);

  const [isProductSelected, setIsProductSelected] = useState(false);

  const [typeSearchText, setTypeSearchText] = useState('');
  const [categorySearchText, setCategorySearchText] = useState('');
  const [variationSearchText, setVariationSearchText] = useState('');
  const [companySearchText, setCompanySearchText] = useState('');
  const [connectionSearchText, setConnectionSearchText] = useState('');

  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showVariationDropdown, setShowVariationDropdown] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [showConnectionDropdown, setShowConnectionDropdown] = useState(false);

  const [customTypeValue, setCustomTypeValue] = useState('');
  const [customCategoryValue, setCustomCategoryValue] = useState('');
  const [customVariationValue, setCustomVariationValue] = useState('');
  const [customCompanyValue, setCustomCompanyValue] = useState('');
  const [customConnectionValue, setCustomConnectionValue] = useState('');

  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  // ✅ Product tab hi dikhao jab Category + Variation dono select ho chuki hon
  const filtersReady = !!(currentProduct.selectCategory && currentProduct.selectVariations);

  // Koi filter dropdown khula ho to product dropdown us par overlap na kare
  const filterDropdownOpenRef = useRef(false);
  filterDropdownOpenRef.current =
    showTypeDropdown || showCategoryDropdown || showVariationDropdown || showCompanyDropdown || showConnectionDropdown;

  const [applyDeliveryCharges, setApplyDeliveryCharges] = useState(false);
  const [applyVAT, setApplyVAT] = useState(false);
  const [deliveryCharges, setDeliveryCharges] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [customerReference, setCustomerReference] = useState('');

  const [customerReferences, setCustomerReferences] = useState([]);

  const [deliveryMethod, setDeliveryMethod] = useState('Same Day');

  const [courierService, setCourierService] = useState('');
  const [courierServiceMode, setCourierServiceMode] = useState('select');
  const [manualCourierService, setManualCourierService] = useState('');
  const [showCourierDropdown, setShowCourierDropdown] = useState(false);
  const [courierSearchText, setCourierSearchText] = useState('');

  // ✅ User ki add ki hui couriers (is browser me save hoti hain)
  const [savedCouriers, setSavedCouriers] = useState(loadSavedCouriers);

  const availableCourierServices = [
    ...DEFAULT_COURIER_SERVICES,
    ...savedCouriers.filter(
      (s) => !DEFAULT_COURIER_SERVICES.some((d) => d.toLowerCase() === s.toLowerCase())
    )
  ];

  // Search text khali ho to saari list dikhao, warna filter
  const filteredCourierServices = availableCourierServices.filter(service =>
    courierSearchText.trim() === '' || service.toLowerCase().includes(courierSearchText.toLowerCase())
  );

  const saveCourierIfNew = (name) => {
    const clean = String(name || '').trim();
    if (!clean) return;
    setSavedCouriers((prev) => {
      const all = [...DEFAULT_COURIER_SERVICES, ...prev];
      if (all.some((s) => s.toLowerCase() === clean.toLowerCase())) return prev;
      const updated = [...prev, clean];
      try { localStorage.setItem(COURIER_STORAGE_KEY, JSON.stringify(updated)); } catch (e) {}
      return updated;
    });
  };

  const removeSavedCourier = (name) => {
    setSavedCouriers((prev) => {
      const updated = prev.filter((s) => s !== name);
      try { localStorage.setItem(COURIER_STORAGE_KEY, JSON.stringify(updated)); } catch (e) {}
      return updated;
    });
  };

  const [agentName, setAgentName] = useState('');
  const [agentNumber, setAgentNumber] = useState('');

  const [agentNameMode, setAgentNameMode] = useState('auto');
  const [agentNumberMode, setAgentNumberMode] = useState('auto');
  const [manualAgentName, setManualAgentName] = useState('');
  const [manualAgentNumber, setManualAgentNumber] = useState('');

  const [note, setNote] = useState('');

  const [showCityStockDropdown, setShowCityStockDropdown] = useState(false);

  // ✅ DRAFTS
  const [view, setView] = useState('form'); // 'form' | 'drafts'
  const [drafts, setDrafts] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [saveStatus, setSaveStatus] = useState(''); // '' | 'saving' | 'saved' | 'error'
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const draftIdRef = useRef(null);
  const lastSavedRef = useRef('');
  const savingRef = useRef(false);
  const skipAutosaveRef = useRef(false);
  const latestDraftRef = useRef({ payload: null, hasContent: false });

  useEffect(() => {
    const fetchReferences = async () => {
      if (!selectedShopId) return;
      try {
        const response = await api.get(`/order/getAllorder/?shop_id=${selectedShopId}`);
        let orders = [];
        if (response.data.orders) orders = response.data.orders;
        else if (Array.isArray(response.data)) orders = response.data;

        const refs = orders
          .filter(o => o.customer && o.customer_Reference && o.customer_Reference.trim() !== '')
          .map(o => ({
            customerId: o.customer.id,
            reference: o.customer_Reference.trim()
          }));

        setCustomerReferences(refs);
      } catch (error) {
        console.error('Failed to fetch customer references:', error);
      }
    };
    fetchReferences();
  }, [selectedShopId]);

  const fetchShops = useCallback(async () => {
    try {
      const response = await api.get('/user/shops/');
      let shopsData = [];
      if (response.data.results) shopsData = response.data.results;
      else if (Array.isArray(response.data)) shopsData = response.data;
      else if (response.data.shops) shopsData = response.data.shops;
      setShops(shopsData);
      return shopsData;
    } catch (error) {
      try {
        const response = await api.get('/api/shops/');
        let shopsData = [];
        if (response.data.results) shopsData = response.data.results;
        else if (Array.isArray(response.data)) shopsData = response.data;
        else if (response.data.shops) shopsData = response.data.shops;
        setShops(shopsData);
        return shopsData;
      } catch (err) { return []; }
    }
  }, []);

  useEffect(() => { fetchShops(); }, [fetchShops]);

  useEffect(() => {
    if (shops.length === 0) return;
    const match = location.pathname.match(/\/shop\/(\d+)/);
    const storedShopId = localStorage.getItem('selectedShopId');
    let resolvedId = '';
    if (match) resolvedId = match[1];
    else if (storedShopId) resolvedId = storedShopId;
    else {
      const firstShop = shops[0];
      resolvedId = String(firstShop.shop_id || firstShop.id || firstShop._id);
    }
    if (resolvedId && String(resolvedId) !== String(selectedShopId)) setSelectedShopId(String(resolvedId));
  }, [shops, location.pathname, selectedShopId]);

  useEffect(() => {
    if (!selectedShopId || shops.length === 0) return;
    const shop = shops.find(s => String(s.shop_id || s.id || s._id) === String(selectedShopId));
    if (shop) {
      const shopName = shop.shop_name || shop.name || 'Unknown Shop';
      setSelectedShopName(shopName);
      localStorage.setItem('selectedShopId', String(selectedShopId));
      localStorage.setItem('selectedShopName', shopName);
      if (shop.cities && shop.cities.length > 0) {
        setCities(shop.cities);
        if (selectedCity !== 'all' && !shop.cities.includes(selectedCity)) setSelectedCity('all');
      } else setCities([]);
    } else if (shops.length > 0) {
      const firstShop = shops[0];
      const firstId = String(firstShop.shop_id || firstShop.id || firstShop._id);
      if (firstId !== String(selectedShopId)) setSelectedShopId(firstId);
    }
  }, [selectedShopId, shops, selectedCity]);

  const fetchCustomers = useCallback(async () => {
    if (!selectedShopId) return;
    try {
      setFetchError(null);
      const response = await api.get(`/user/getAllcustomers/?shop_id=${selectedShopId}`);
      let customersData = [];
      if (Array.isArray(response.data)) customersData = response.data;
      else if (response.data.results) customersData = response.data.results;
      else if (response.data.customers) customersData = response.data.customers;
      else if (response.data.data) customersData = response.data.data;
      const formattedCustomers = customersData.map(customer => ({
        id: customer.id, user_id: customer.id, customer_id: customer.id,
        first_Name: customer.first_Name || customer.firstName || '',
        last_Name: customer.last_Name || customer.lastName || '',
        email: customer.email || '',
        phone_Number: customer.phone_Number || customer.phone || '',
        shop: customer.shop || null, shop_name: customer.shop_name || 'N/A',
        selected_city: customer.selected_city || '',
        addresses: customer.addresses || [],
        address_details: customer.address_details || null,
        address: customer.address || null
      }));
      setCustomers(formattedCustomers);
    } catch (error) {
      setFetchError('Failed to fetch customers');
    }
  }, [selectedShopId]);

  const fetchAddresses = useCallback(async () => {
    if (!selectedShopId) return;
    try {
      const response = await api.get(`/user/addresses/?shop_id=${selectedShopId}`);
      let addressesData = [];
      if (response.data && response.data.addresses) addressesData = response.data.addresses;
      else if (response.data.results) addressesData = response.data.results;
      else if (Array.isArray(response.data)) addressesData = response.data;
      else if (response.data.data) addressesData = response.data.data;
      setAddresses(addressesData);
    } catch (error) {
      console.error('Error fetching addresses:', error);
    }
  }, [selectedShopId]);

  const fetchFilteredProductData = useCallback(async (shopId) => {
    if (!shopId) return;
    try {
      const [typesRes, variationsRes, categoriesRes, implantCompaniesRes, brandsRes] = await Promise.allSettled([
        api.get(`/api/product/types/?shop_id=${shopId}`),
        api.get(`/api/product/variations/?shop_id=${shopId}`),
        api.get(`/api/product/categories/?shop_id=${shopId}`),
        api.get(`/api/product/implant-companies/?shop_id=${shopId}`),
        api.get(`/api/product/brands/?shop_id=${shopId}`)
      ]);
      const handleResponse = (result) => {
        if (result.status === 'fulfilled') {
          const data = result.value.data;
          if (Array.isArray(data)) return data;
          if (data.results) return data.results;
          if (data.data) return data.data;
        }
        return [];
      };
      setTypes(handleResponse(typesRes));
      setVariations(handleResponse(variationsRes));
      setCategories(handleResponse(categoriesRes));
      setImplantCompanies(handleResponse(implantCompaniesRes));
      setBrands(handleResponse(brandsRes));
    } catch (error) {
      console.error('Error fetching filtered product data:', error);
    }
  }, []);

  const handleCityChange = (e) => {
    setSelectedCity(e.target.value);
  };

  useEffect(() => {
    if (!selectedShopId) return;
    setOrderDetails(prev => ({ ...prev, customer: '', phoneNumber: '', address: '' }));
    setOrderedProducts([]);
    setActiveRowId(null);
    setCurrentProduct(EMPTY_PRODUCT);
    setTypeSearchText(''); setCategorySearchText(''); setVariationSearchText('');
    setCompanySearchText(''); setConnectionSearchText('');
    setCustomTypeValue(''); setCustomCategoryValue(''); setCustomVariationValue('');
    setCustomCompanyValue(''); setCustomConnectionValue('');
    setSelectedCity('all'); setSearchResults([]); setShowSearchDropdown(false);
    setIsProductSelected(false);

    // Shop badalne par current draft us shop ka tha — usay delete kiye baghair form alag kar do
    draftIdRef.current = null;
    lastSavedRef.current = '';
    setDraftId(null);
    setSaveStatus('');
    setView('form');

    fetchCustomers();
    fetchAddresses();
    fetchFilteredProductData(selectedShopId);
  }, [selectedShopId, fetchCustomers, fetchAddresses, fetchFilteredProductData]);

  useEffect(() => {
    const handleShopChanged = (event) => {
      const { shopId } = event.detail;
      if (shopId) setSelectedShopId(String(shopId));
    };
    window.addEventListener('shopChanged', handleShopChanged);
    return () => window.removeEventListener('shopChanged', handleShopChanged);
  }, []);

  useEffect(() => {
    const loggedInUser = getLoggedInUser();
    if (loggedInUser) {
      const firstName = loggedInUser.first_Name || loggedInUser.firstName || '';
      const lastName = loggedInUser.last_Name || loggedInUser.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) setAgentName(fullName);
    }
  }, []);

  useEffect(() => {
    if (selectedShopId && shops.length > 0) {
      const shop = shops.find(s => String(s.shop_id || s.id) === String(selectedShopId));
      if (shop && (shop.shop_phone || shop.phone)) setAgentNumber(shop.shop_phone || shop.phone || '');
    }
  }, [selectedShopId, shops]);

  const showSuccessAlert = (title, text = '') => Swal.fire({ icon: 'success', title, text, confirmButtonColor: '#004aad', timer: 3000, timerProgressBar: true, showConfirmButton: false });
  const showErrorAlert = (title, text = '') => Swal.fire({ icon: 'error', title, text, confirmButtonColor: '#004aad' });
  const showWarningAlert = (title, text = '') => Swal.fire({ icon: 'warning', title, text, confirmButtonColor: '#004aad' });
  const showInfoAlert = (title, text = '') => Swal.fire({ icon: 'info', title, text, confirmButtonColor: '#004aad', timer: 2000, timerProgressBar: true, showConfirmButton: false });
  const showLoadingAlert = (title = 'Processing...', text = 'Please wait') => Swal.fire({ title, text, allowOutsideClick: false, allowEscapeKey: false, showConfirmButton: false, didOpen: () => { Swal.showLoading(); } });
  const showConfirmAlert = async (title, text = '', confirmText = 'Yes', cancelText = 'Cancel') => await Swal.fire({ title, text, icon: 'question', showCancelButton: true, confirmButtonColor: '#004aad', cancelButtonColor: '#d33', confirmButtonText: confirmText, cancelButtonText: cancelText });

  const handleClickOutside = useCallback((event) => {
    if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
      setShowSearchDropdown(false);
    }
  }, []);

  useEffect(() => {
    const handleClickOutsideDropdowns = (event) => {
      if (event.target.closest('.searchable-filter-container') || event.target.closest('.courier-search-container') || event.target.closest('.city-stock-container')) return;
      setShowTypeDropdown(false); setShowCategoryDropdown(false); setShowVariationDropdown(false);
      setShowCompanyDropdown(false); setShowConnectionDropdown(false); setShowCourierDropdown(false); setShowCityStockDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutsideDropdowns);
    return () => document.removeEventListener('mousedown', handleClickOutsideDropdowns);
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const searchProducts = useCallback(async () => {
    if (!selectedShopId) return;
    // ✅ Category + Variation select hone tak koi product search nahi
    if (!currentProduct.selectCategory || !currentProduct.selectVariations) return;

    const requestId = ++searchRequestRef.current;
    setSearchLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('shop_id', selectedShopId);
      params.append('page_size', '500');
      if (selectedCity && selectedCity !== 'all') params.append('city', selectedCity);

      if (currentProduct.selectType) {
        const selectedType = types.find(t => t.type_id === parseInt(currentProduct.selectType));
        if (selectedType) params.append('type_name', selectedType.type_name);
      } else if (customTypeValue) {
        params.append('type_name', customTypeValue);
      }

      if (currentProduct.selectCategory) {
        const selectedCategory = categories.find(c => c.catrgort_id === parseInt(currentProduct.selectCategory));
        if (selectedCategory) params.append('category_Name', selectedCategory.category_Name);
      } else if (customCategoryValue) {
        params.append('category_Name', customCategoryValue);
      }

      if (currentProduct.selectVariations) {
        const selectedVariation = variations.find(v => v.variation_id === parseInt(currentProduct.selectVariations));
        if (selectedVariation) params.append('variation_name', selectedVariation.variation_name);
      } else if (customVariationValue) {
        params.append('variation_name', customVariationValue);
      }

      if (currentProduct.selectImplantCompany) {
        const selectedCompany = implantCompanies.find(c => c.company_id === parseInt(currentProduct.selectImplantCompany));
        if (selectedCompany) params.append('implant_Company', selectedCompany.company_name);
      } else if (customCompanyValue) {
        params.append('implant_Company', customCompanyValue);
      }

      if (currentProduct.selectImplantConnection) {
        const selectedBrand = brands.find(b => b.brand_id === parseInt(currentProduct.selectImplantConnection));
        if (selectedBrand) params.append('brand_Name', selectedBrand.brand_name);
      } else if (customConnectionValue) {
        params.append('brand_Name', customConnectionValue);
      }

      if (currentProduct.productSearch) params.append('search', currentProduct.productSearch);

      const filters = {};

      if (currentProduct.selectType) {
        const t = types.find(x => String(x.type_id) === String(currentProduct.selectType));
        filters.type = { id: currentProduct.selectType, name: t?.type_name };
      } else if (customTypeValue.trim()) {
        filters.type = { name: customTypeValue };
      }

      if (currentProduct.selectCategory) {
        const c = categories.find(x => String(x.catrgort_id) === String(currentProduct.selectCategory));
        filters.category = { id: currentProduct.selectCategory, name: c?.category_Name };
      } else if (customCategoryValue.trim()) {
        filters.category = { name: customCategoryValue };
      }

      if (currentProduct.selectVariations) {
        const v = variations.find(x => String(x.variation_id) === String(currentProduct.selectVariations));
        filters.variation = { id: currentProduct.selectVariations, name: v?.variation_name };
      } else if (customVariationValue.trim()) {
        filters.variation = { name: customVariationValue };
      }

      if (currentProduct.selectImplantCompany) {
        const co = implantCompanies.find(x => String(x.company_id) === String(currentProduct.selectImplantCompany));
        filters.company = { id: currentProduct.selectImplantCompany, name: co?.company_name };
      } else if (customCompanyValue.trim()) {
        filters.company = { name: customCompanyValue };
      }

      if (currentProduct.selectImplantConnection) {
        const b = brands.find(x => String(x.brand_id) === String(currentProduct.selectImplantConnection));
        filters.brand = { id: currentProduct.selectImplantConnection, name: b?.brand_name };
      } else if (customConnectionValue.trim()) {
        filters.brand = { name: customConnectionValue };
      }

      // 1) Backend ko saare filters ke saath try karo (saare pages)
      const raw = await fetchAllProductPages(params);
      if (requestId !== searchRequestRef.current) return;
      let list = filterProductsClientSide(raw, filters);
      let rawLooseCount = null;

      // 2) Khali aaye to backend ke filters hata kar dobara lo, aur filter yahan client-side karo
      //    (agar backend category_Name / variation_name / city ko apni tarah handle kar raha ho)
      if (list.length === 0) {
        const loose = new URLSearchParams();
        loose.append('shop_id', selectedShopId);
        loose.append('page_size', '500');
        if (currentProduct.productSearch) loose.append('search', currentProduct.productSearch);

        const rawLoose = await fetchAllProductPages(loose);
        if (requestId !== searchRequestRef.current) return;
        rawLooseCount = rawLoose.length;
        list = filterProductsClientSide(rawLoose, filters);
      }

      console.debug('[Place Order] product search', {
        strictFetched: raw.length,
        looseFetched: rawLooseCount,
        shown: list.length,
        filters,
        city: selectedCity,
        sampleProduct: (raw[0] || null)
      });

      setSearchResults(list);
      // Agar koi filter dropdown khula hai to product dropdown mat kholo (overlap)
      setShowSearchDropdown(!filterDropdownOpenRef.current);
    } catch (error) {
      if (requestId === searchRequestRef.current) {
        console.error('Error searching products:', error);
        setSearchResults([]);
      }
    } finally {
      if (requestId === searchRequestRef.current) setSearchLoading(false);
    }
  }, [selectedShopId, currentProduct, types, categories, variations, implantCompanies, brands, customTypeValue, customCategoryValue, customVariationValue, customCompanyValue, customConnectionValue, selectedCity]);

  useEffect(() => {
    if (!selectedShopId) return;
    if (isProductSelected) return;

    if (!filtersReady) {
      setSearchResults((prev) => (prev.length ? [] : prev));
      setShowSearchDropdown(false);
      return;
    }

    const delayDebounceFn = setTimeout(() => {
      searchProducts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [currentProduct, searchProducts, selectedShopId, customTypeValue, customCategoryValue, customVariationValue, customCompanyValue, customConnectionValue, isProductSelected, selectedCity, filtersReady]);

  useEffect(() => {
    setIsProductSelected(false);
  }, [
    currentProduct.selectType, currentProduct.selectCategory, currentProduct.selectVariations,
    currentProduct.selectImplantCompany, currentProduct.selectImplantConnection,
    customTypeValue, customCategoryValue, customVariationValue, customCompanyValue, customConnectionValue,
    selectedCity
  ]);

  const handleInputChange = (field, value) => setOrderDetails(prev => ({ ...prev, [field]: value }));
  const handleOrderPlacementDateChange = (value) => {
    setOrderDetails(prev => {
      const updated = { ...prev, orderPlacementDate: value };
      if (!prev.orderDeliveryDate || prev.orderDeliveryDate === prev.orderPlacementDate) updated.orderDeliveryDate = value;
      return updated;
    });
  };
  const handleOrderDeliveryDateChange = (value) => setOrderDetails(prev => ({ ...prev, orderDeliveryDate: value }));

  const isValidAddress = (address) => {
    if (!address) return false;
    const area = address.area || ''; const street = address.street_Number || address.streetNumber || ''; const house = address.house_Number || address.houseNumber || '';
    if (area === '0' && street === '0' && house === '0') return false;
    if (area === '' && street === '' && house === '') return false;
    if (!address.city || address.city === '') return false;
    return true;
  };

  const handleCustomerChange = (customerId) => {
    const selectedCustomer = customers.find(c => String(c.id || c.user_id || c.customer_id) === String(customerId));
    if (selectedCustomer) {
      const phoneNumber = selectedCustomer.phone_Number || selectedCustomer.phone || '';
      let autoSelectedAddress = '';
      const customerAddresses = selectedCustomer.addresses || [];
      const firstValidAddress = customerAddresses.find(addr => isValidAddress(addr));
      if (firstValidAddress) autoSelectedAddress = firstValidAddress.address_Id || firstValidAddress.id || '';
      if (!autoSelectedAddress && selectedCustomer.address_details && isValidAddress(selectedCustomer.address_details)) autoSelectedAddress = selectedCustomer.address_details.address_Id || selectedCustomer.address_details.id || '';
      if (!autoSelectedAddress && selectedCustomer.address && isValidAddress(selectedCustomer.address)) autoSelectedAddress = selectedCustomer.address.address_Id || selectedCustomer.address.id || '';
      if (!autoSelectedAddress) {
        const custId = selectedCustomer.id || selectedCustomer.user_id || selectedCustomer.customer_id;
        const addressFromList = addresses.find(addr => addr.customer === custId || addr.customer_id === custId || (addr.customer && addr.customer.id === custId));
        if (addressFromList && isValidAddress(addressFromList)) autoSelectedAddress = addressFromList.address_Id || addressFromList.id || '';
      }

      // ✅ Customer ki city auto-select (address ki city pehle, phir selected_city)
      let autoCity = 'all';
      if (autoSelectedAddress) {
        const addrObj = customerAddresses.find(a => String(a.address_Id || a.id) === String(autoSelectedAddress))
          || (selectedCustomer.address_details && String(selectedCustomer.address_details.address_Id || selectedCustomer.address_details.id) === String(autoSelectedAddress) ? selectedCustomer.address_details : null)
          || (selectedCustomer.address && String(selectedCustomer.address.address_Id || selectedCustomer.address.id) === String(autoSelectedAddress) ? selectedCustomer.address : null)
          || addresses.find(a => String(a.address_Id || a.id) === String(autoSelectedAddress));
        if (addrObj && addrObj.city) autoCity = addrObj.city;
      }
      if (autoCity === 'all' && selectedCustomer.selected_city) autoCity = selectedCustomer.selected_city;

      if (autoCity && autoCity !== 'all' && cities.length > 0 && !cities.includes(autoCity)) {
        autoCity = 'all';
      }

      setSelectedCity(autoCity);
      setOrderDetails(prev => ({ ...prev, customer: customerId, phoneNumber: phoneNumber, address: autoSelectedAddress }));
    } else {
      setSelectedCity('all');
      setOrderDetails(prev => ({ ...prev, customer: customerId, phoneNumber: '', address: '' }));
    }
  };

  const getCustomerAddresses = () => {
    if (!orderDetails.customer) return addresses.filter(isValidAddress);
    const selectedCustomer = customers.find(c => String(c.id || c.user_id || c.customer_id) === String(orderDetails.customer));
    if (!selectedCustomer) return addresses.filter(isValidAddress);
    const customerId = selectedCustomer.id || selectedCustomer.user_id || selectedCustomer.customer_id;
    const uniqueAddresses = []; const seenIds = new Set();
    if (selectedCustomer.addresses && selectedCustomer.addresses.length > 0) {
      selectedCustomer.addresses.forEach(addr => {
        const addrId = addr.address_Id || addr.id;
        if (!isValidAddress(addr) || seenIds.has(addrId)) return;
        uniqueAddresses.push(addr); seenIds.add(addrId);
      });
    }
    if (selectedCustomer.address_details && isValidAddress(selectedCustomer.address_details)) {
      const addrId = selectedCustomer.address_details.address_Id || selectedCustomer.address_details.id;
      if (!seenIds.has(addrId)) { uniqueAddresses.push(selectedCustomer.address_details); seenIds.add(addrId); }
    }
    if (selectedCustomer.address && isValidAddress(selectedCustomer.address)) {
      const addrId = selectedCustomer.address.address_Id || selectedCustomer.address.id;
      if (!seenIds.has(addrId)) { uniqueAddresses.push(selectedCustomer.address); seenIds.add(addrId); }
    }
    addresses.forEach(addr => {
      const addrId = addr.address_Id || addr.id;
      const isCustomerAddress = addr.customer === customerId || addr.customer_id === customerId || (addr.customer && addr.customer.id === customerId);
      if (isCustomerAddress && isValidAddress(addr) && !seenIds.has(addrId)) { uniqueAddresses.push(addr); seenIds.add(addrId); }
    });
    return uniqueAddresses;
  };

  const handleProductChange = (field, value) => {
    setCurrentProduct(prev => {
      let updated = { ...prev, [field]: value };
      if (field === 'quantity') {
        let qty = parseInt(value) || 0;
        if (qty < 0) qty = 0;
        updated = { ...prev, quantity: qty };
      }
      if (updated.quantity && updated.productPrice) {
        updated.total = (parseFloat(updated.quantity) * parseFloat(updated.productPrice)).toFixed(2);
      }
      return updated;
    });
  };

  // ✅ Quantity badlo to neeche table row + totals foran update
  const handleQuantityChange = (value) => {
    let qty = parseInt(value) || 0;
    if (qty < 0) qty = 0;

    const max = currentProduct.selectedCityStock
      ? currentProduct.selectedCityStock.stock
      : currentProduct.availableStock;
    if (max !== null && max !== undefined && qty > max) qty = max;

    const total = (qty * parseFloat(currentProduct.productPrice || 0)).toFixed(2);

    setCurrentProduct(prev => ({ ...prev, quantity: qty, total }));

    if (activeRowId) {
      setOrderedProducts(prev =>
        prev.map(p => (p.id === activeRowId ? { ...p, quantity: qty, total } : p))
      );
    }
  };

  const handleCityStockSelect = (selectedItem) => {
    const newQty = selectedItem.stock > 0 ? 1 : 0;
    const total = (newQty * parseFloat(currentProduct.productPrice || 0)).toFixed(2);

    setCurrentProduct(prev => ({ ...prev, selectedCityStock: selectedItem, quantity: newQty, total }));
    if (activeRowId) {
      setOrderedProducts(prev =>
        prev.map(p => (p.id === activeRowId ? { ...p, quantity: newQty, total, city: selectedItem.city } : p))
      );
    }
    setShowCityStockDropdown(false);
  };

  const handleTypeSearchChange = (value) => { setTypeSearchText(value); setCustomTypeValue(value); setCurrentProduct(prev => ({ ...prev, selectType: '' })); setShowSearchDropdown(false); setShowTypeDropdown(true); };
  const handleTypeClick = () => { setShowTypeDropdown(prev => !prev); setShowSearchDropdown(false); setShowCategoryDropdown(false); setShowVariationDropdown(false); setShowCompanyDropdown(false); setShowConnectionDropdown(false); setShowCourierDropdown(false); setShowCityStockDropdown(false); };

  const handleCategorySearchChange = (value) => { setCategorySearchText(value); setCustomCategoryValue(value); setCurrentProduct(prev => ({ ...prev, selectCategory: '' })); setShowSearchDropdown(false); setShowCategoryDropdown(true); };
  const handleCategoryClick = () => { setShowCategoryDropdown(prev => !prev); setShowSearchDropdown(false); setShowTypeDropdown(false); setShowVariationDropdown(false); setShowCompanyDropdown(false); setShowConnectionDropdown(false); setShowCourierDropdown(false); setShowCityStockDropdown(false); };

  const handleVariationSearchChange = (value) => { setVariationSearchText(value); setCustomVariationValue(value); setCurrentProduct(prev => ({ ...prev, selectVariations: '' })); setShowSearchDropdown(false); setShowVariationDropdown(true); };
  const handleVariationClick = () => { setShowVariationDropdown(prev => !prev); setShowSearchDropdown(false); setShowTypeDropdown(false); setShowCategoryDropdown(false); setShowCompanyDropdown(false); setShowConnectionDropdown(false); setShowCourierDropdown(false); setShowCityStockDropdown(false); };

  const handleCompanySearchChange = (value) => { setCompanySearchText(value); setCustomCompanyValue(value); setCurrentProduct(prev => ({ ...prev, selectImplantCompany: '' })); setShowSearchDropdown(false); setShowCompanyDropdown(true); };
  const handleCompanyClick = () => { setShowCompanyDropdown(prev => !prev); setShowSearchDropdown(false); setShowTypeDropdown(false); setShowCategoryDropdown(false); setShowVariationDropdown(false); setShowConnectionDropdown(false); setShowCourierDropdown(false); setShowCityStockDropdown(false); };

  const handleConnectionSearchChange = (value) => { setConnectionSearchText(value); setCustomConnectionValue(value); setCurrentProduct(prev => ({ ...prev, selectImplantConnection: '' })); setShowSearchDropdown(false); setShowConnectionDropdown(true); };
  const handleConnectionClick = () => { setShowConnectionDropdown(prev => !prev); setShowSearchDropdown(false); setShowTypeDropdown(false); setShowCategoryDropdown(false); setShowVariationDropdown(false); setShowCompanyDropdown(false); setShowCourierDropdown(false); setShowCityStockDropdown(false); };

  const handleCourierServiceClick = () => { setShowCourierDropdown(prev => !prev); setShowTypeDropdown(false); setShowCategoryDropdown(false); setShowVariationDropdown(false); setShowCompanyDropdown(false); setShowConnectionDropdown(false); setShowCityStockDropdown(false); };
  // ✅ Jo type karo wahi courier value ban jaye (pehle sirf list se select karne par set hoti thi)
  const handleCourierSearchChange = (value) => { setCourierSearchText(value); setCourierService(value); setShowCourierDropdown(true); };
  const selectCourierOption = (service) => { setCourierService(service); setCourierSearchText(service); setManualCourierService(''); setCourierServiceMode('select'); setShowCourierDropdown(false); };
  const switchToManualCourier = () => { if (courierService) setManualCourierService(courierService); setCourierServiceMode('manual'); setShowCourierDropdown(false); };
  const switchToSelectCourier = () => { setCourierServiceMode('select'); setManualCourierService(''); if (courierService) setCourierSearchText(courierService); };

  const selectTypeOption = (type) => {
    setCurrentProduct(prev => ({ ...prev, selectType: String(type.type_id) }));
    setTypeSearchText(type.type_name);
    setCustomTypeValue(type.type_name);
    setShowTypeDropdown(false);
  };

  const selectCategoryOption = (category) => {
    const categoryId = String(category.catrgort_id);

    const currentVariation = currentProduct.selectVariations
      ? variations.find(v => String(v.variation_id) === String(currentProduct.selectVariations))
      : null;
    const variationBelongs =
      !currentVariation ||
      filterVariationsByCategory([currentVariation], categoryId, category.category_Name, '').length > 0;

    if (!variationBelongs) {
      setVariationSearchText('');
      setCustomVariationValue('');
    }

    setCurrentProduct(prev => ({
      ...prev,
      selectCategory: categoryId,
      ...(variationBelongs ? {} : { selectVariations: '' })
    }));
    setCategorySearchText(category.category_Name);
    setCustomCategoryValue(category.category_Name);
    setShowCategoryDropdown(false);
  };

  const selectVariationOption = (variation) => {
    setCurrentProduct(prev => ({ ...prev, selectVariations: String(variation.variation_id) }));
    setVariationSearchText(variation.variation_name);
    setCustomVariationValue(variation.variation_name);
    setShowVariationDropdown(false);
  };

  const selectCompanyOption = (company) => {
    const companyId = String(company.company_id);

    const currentBrand = currentProduct.selectImplantConnection
      ? brands.find(b => String(b.brand_id) === String(currentProduct.selectImplantConnection))
      : null;
    const brandCompanyId = currentBrand ? getBrandCompanyId(currentBrand) : null;
    const brandBelongs = !currentBrand || brandCompanyId == null || String(brandCompanyId) === companyId;

    if (!brandBelongs) {
      setConnectionSearchText('');
      setCustomConnectionValue('');
    }

    setCurrentProduct(prev => ({
      ...prev,
      selectImplantCompany: companyId,
      ...(brandBelongs ? {} : { selectImplantConnection: '' })
    }));
    setCompanySearchText(company.company_name);
    setCustomCompanyValue(company.company_name);
    setShowCompanyDropdown(false);
  };

  const selectConnectionOption = (brand) => {
    setCurrentProduct(prev => ({ ...prev, selectImplantConnection: String(brand.brand_id) }));
    setConnectionSearchText(brand.brand_name);
    setCustomConnectionValue(brand.brand_name);
    setShowConnectionDropdown(false);
  };

  const filteredTypes = types.filter(type => type.type_name.toLowerCase().includes(typeSearchText.toLowerCase()));
  const filteredCategories = categories.filter(category => category.category_Name.toLowerCase().includes(categorySearchText.toLowerCase()));

  const selectedCategoryObj = currentProduct.selectCategory
    ? categories.find(c => String(c.catrgort_id) === String(currentProduct.selectCategory))
    : null;
  const variationsForCategory = filterVariationsByCategory(
    variations,
    currentProduct.selectCategory,
    selectedCategoryObj?.category_Name,
    customCategoryValue
  );
  const filteredVariations = variationsForCategory.filter(variation => (variation.variation_name || '').toLowerCase().includes(variationSearchText.toLowerCase()));

  const filteredCompanies = implantCompanies.filter(company => company.company_name.toLowerCase().includes(companySearchText.toLowerCase()));
  const selectedCompanyObj = currentProduct.selectImplantCompany
    ? implantCompanies.find(c => String(c.company_id) === String(currentProduct.selectImplantCompany))
    : null;
  const brandsForCompany = filterBrandsByCompany(
    brands,
    currentProduct.selectImplantCompany,
    selectedCompanyObj?.company_name,
    customCompanyValue
  );
  const filteredConnections = brandsForCompany.filter(brand => (brand.brand_name || '').toLowerCase().includes(connectionSearchText.toLowerCase()));

  const hasActiveFilter = !!(
    currentProduct.selectType || currentProduct.selectCategory || currentProduct.selectVariations ||
    currentProduct.selectImplantCompany || currentProduct.selectImplantConnection ||
    customTypeValue || customCategoryValue || customVariationValue || customCompanyValue || customConnectionValue
  );

  // ✅ Dropdown se product select hotay hi order me add
  const selectProduct = (product) => {
    let productCityStock = [];
    if (product.city_stocks && Array.isArray(product.city_stocks)) {
      productCityStock = product.city_stocks.map(cs => ({ city: cs.city, stock: parseInt(cs.stock) || 0 }));
    } else if (product.cities_stock && Array.isArray(product.cities_stock)) {
      productCityStock = product.cities_stock.map(cs => ({ city: cs.city, stock: parseInt(cs.stock) || 0 }));
    } else if (product.stock_by_city && Array.isArray(product.stock_by_city)) {
      productCityStock = product.stock_by_city.map(cs => ({ city: cs.city, stock: parseInt(cs.stock) || 0 }));
    } else {
      const currentSelectedCity = selectedCity !== 'all' ? selectedCity : 'Default';
      productCityStock = [{ city: currentSelectedCity, stock: parseInt(product.product_Stock) || 0 }];
    }

    let finalStock = 0;
    let initialSelectedCity = null;

    if (selectedCity !== 'all') {
      const matched = productCityStock.find(c => c.city.toLowerCase() === selectedCity.toLowerCase());
      finalStock = matched ? matched.stock : 0;
      initialSelectedCity = matched || productCityStock[0] || null;
    } else {
      finalStock = productCityStock.reduce((sum, item) => sum + item.stock, 0);
      initialSelectedCity = productCityStock.find(cs => cs.stock > 0) || productCityStock[0] || null;
    }

    const initialQty = initialSelectedCity && initialSelectedCity.stock > 0 ? 1 : 0;
    const price = parseFloat(product.product_Price) || 0;
    const total = (initialQty * price).toFixed(2);

    setCurrentProduct(prev => ({
      ...prev,
      productSearch: product.product_Name,
      productPrice: product.product_Price,
      productId: product.product_Id,
      availableStock: finalStock,
      quantity: initialQty,
      cityStock: productCityStock,
      selectedCityStock: initialSelectedCity,
      total
    }));

    setIsProductSelected(true);
    setShowSearchDropdown(false);
    setShowCityStockDropdown(false);

    // Out of stock: add mat karo, sirf warning
    if (initialQty < 1) {
      setActiveRowId(null);
      showWarningAlert('Out of Stock', `${product.product_Name} has no remaining stock available to order.`);
      return;
    }

    // Agar product pehle se order me hai to nayi row mat banao, usi ko quantity se link kar do
    const existing = orderedProducts.find(p => String(p.product_Id) === String(product.product_Id));
    if (existing) {
      setActiveRowId(existing.id);
      setCurrentProduct(prev => ({ ...prev, quantity: existing.quantity, total: existing.total }));
      showInfoAlert('Already in order', 'Quantity change karo to wahi row update hogi.');
      return;
    }

    const rowId = Date.now();
    setOrderedProducts(prev => [...prev, {
      id: rowId,
      product_Id: product.product_Id,
      productName: product.product_Name,
      quantity: initialQty,
      productPrice: product.product_Price,
      city: initialSelectedCity ? initialSelectedCity.city : '',
      total
    }]);
    setActiveRowId(rowId);
  };

  // =====================================================================
  // RESET HELPERS
  // =====================================================================
  const clearProductFilterInputs = () => {
    setTypeSearchText(''); setCategorySearchText(''); setVariationSearchText('');
    setCompanySearchText(''); setConnectionSearchText('');
    setCustomTypeValue(''); setCustomCategoryValue(''); setCustomVariationValue('');
    setCustomCompanyValue(''); setCustomConnectionValue('');
  };

  const resetAgentFields = () => {
    setAgentNameMode('auto'); setAgentNumberMode('auto'); setManualAgentName(''); setManualAgentNumber('');
    const loggedInUser = getLoggedInUser();
    if (loggedInUser) {
      const firstName = loggedInUser.first_Name || loggedInUser.firstName || '';
      const lastName = loggedInUser.last_Name || loggedInUser.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) setAgentName(fullName);
    }
    const currentShop = shops.find(s => String(s.shop_id || s.id) === String(selectedShopId));
    if (currentShop && (currentShop.shop_phone || currentShop.phone)) setAgentNumber(currentShop.shop_phone || currentShop.phone || '');
  };

  // keepOrderDetails = true  -> customer/phone/address/dates waise hi rehte hain (Reset Filters)
  // keepOrderDetails = false -> poora form khali (order place hone ke baad / new order)
  const resetOrderForm = (keepOrderDetails = false) => {
    setOrderedProducts([]);
    setActiveRowId(null);
    setCurrentProduct(EMPTY_PRODUCT);
    clearProductFilterInputs();
    setDeliveryCharges(0); setDiscount(0); setApplyDeliveryCharges(false); setApplyVAT(false); setCustomerReference('');
    setDeliveryMethod('Same Day');
    setCourierService(''); setCourierServiceMode('select'); setManualCourierService(''); setCourierSearchText('');
    resetAgentFields();
    setNote(''); setSearchResults([]); setShowSearchDropdown(false);
    setIsProductSelected(false);
    setSelectedCity('all'); setAddressMode('select'); setManualAddress('');
    if (!keepOrderDetails) setOrderDetails(freshOrderDetails());
  };

  // ✅ Ab ye button sirf NAYI product ke liye form clear karta hai
  const addProduct = () => {
    setCurrentProduct(EMPTY_PRODUCT);
    setActiveRowId(null);
    clearProductFilterInputs();
    setSearchResults([]); setShowSearchDropdown(false);
    setIsProductSelected(false);
  };

  const removeProduct = async (id) => {
    const result = await showConfirmAlert('Remove Product', 'Are you sure you want to remove this product from the order?', 'Yes, Remove', 'Cancel');
    if (result.isConfirmed) {
      if (id === activeRowId) setActiveRowId(null);
      setOrderedProducts(orderedProducts.filter(product => product.id !== id));
      showSuccessAlert('Product Removed', 'Product has been removed from the order successfully!');
    }
  };

  const resetFilters = async () => {
    const result = await showConfirmAlert('Reset Filters', 'This will clear all products and reset all filters. Are you sure?', 'Yes, Reset', 'Cancel');
    if (result.isConfirmed) {
      resetOrderForm(true);
      showInfoAlert('Filters Reset', 'All filters and products have been reset successfully!');
    }
  };

  const getAddressValue = (address) => address.address_Id || address.id || address.address_id || '';
  const getAddressDisplay = (address) => {
    if (!address) return 'Unknown Address';
    const houseNo = address.house_Number || address.houseNumber || address.house_number || '';
    const streetNo = address.street_Number || address.streetNumber || address.street_number || '';
    const area = address.area || ''; const city = address.city || ''; const shopName = address.shop_name || '';
    let display = city;
    if (area) display += ` - ${area}`;
    if (streetNo) display += `, St: ${streetNo}`;
    if (houseNo) display += `, House: ${houseNo}`;
    if (shopName) display += ` (${shopName})`;
    return display;
  };

  const switchToManual = () => {
    if (orderDetails.address) {
      const selectedAddr = addresses.find(a => String(getAddressValue(a)) === String(orderDetails.address));
      if (selectedAddr) setManualAddress(getAddressDisplay({ ...selectedAddr, shop_name: '' }));
    }
    setAddressMode('manual');
    setOrderDetails(prev => ({ ...prev, address: '' }));
  };
  const switchToSelect = () => { setAddressMode('select'); setManualAddress(''); };
  const switchAgentNameToManual = () => { setManualAgentName(agentName || ''); setAgentNameMode('manual'); };
  const switchAgentNameToAuto = () => {
    setAgentNameMode('auto'); setManualAgentName('');
    const loggedInUser = getLoggedInUser();
    if (loggedInUser) {
      const firstName = loggedInUser.first_Name || loggedInUser.firstName || '';
      const lastName = loggedInUser.last_Name || loggedInUser.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) setAgentName(fullName);
    }
  };
  const switchAgentNumberToManual = () => { setManualAgentNumber(agentNumber || ''); setAgentNumberMode('manual'); };
  const switchAgentNumberToAuto = () => {
    setAgentNumberMode('auto'); setManualAgentNumber('');
    const currentShop = shops.find(s => String(s.shop_id || s.id) === String(selectedShopId));
    if (currentShop && (currentShop.shop_phone || currentShop.phone)) setAgentNumber(currentShop.shop_phone || currentShop.phone || '');
  };

  const calculateSubtotal = () => orderedProducts.reduce((sum, product) => sum + parseFloat(product.total || 0), 0);
  const calculateVAT = () => applyVAT ? calculateSubtotal() * 0.20 : 0;
  const calculateTotal = () => {
    let total = calculateSubtotal();
    if (applyDeliveryCharges) total += deliveryCharges;
    total += calculateVAT();
    total -= discount;
    return total;
  };

  const getCustomerDisplayName = (customer) => {
    const firstName = customer.first_Name || customer.firstName || '';
    const lastName = customer.last_Name || customer.lastName || '';
    const email = customer.email || '';
    const fullName = `${firstName} ${lastName}`.trim();
    const citySuffix = customer.selected_city ? ` (${customer.selected_city})` : '';
    return fullName ? `${fullName} - ${email}${citySuffix}` : email;
  };

  // Check duplicate ONLY for the currently selected customer (Case & Punctuation Sensitive)
  const isReferenceDuplicate = () => {
    if (!customerReference || customerReference.trim() === '') return false;
    if (!orderDetails.customer) return false;

    const trimmedInput = customerReference.trim();

    return customerReferences.some(ref => {
      if (String(ref.customerId) === String(orderDetails.customer)) {
        return ref.reference === trimmedInput;
      }
      return false;
    });
  };

  // =====================================================================
  // ✅ DRAFTS (email jaisa auto-save, har 3 second)
  // =====================================================================
  const draftData = {
    orderDetails,
    addressMode,
    manualAddress,
    orderedProducts,
    selectedCity,
    applyDeliveryCharges,
    applyVAT,
    deliveryCharges,
    discount,
    customerReference,
    deliveryMethod,
    courierService,
    courierServiceMode,
    manualCourierService,
    agentNameMode,
    manualAgentName,
    agentNumberMode,
    manualAgentNumber,
    note
  };

  const hasDraftContent = !!(
    orderDetails.customer ||
    customerReference.trim() ||
    orderedProducts.length > 0 ||
    note.trim() ||
    manualAddress.trim() ||
    orderDetails.orderTrackingId.trim() ||
    Number(discount) > 0 ||
    (applyDeliveryCharges && Number(deliveryCharges) > 0)
  );

  const draftCustomer = customers.find(c => String(c.id || c.user_id || c.customer_id) === String(orderDetails.customer));
  const draftCustomerName = draftCustomer
    ? (`${draftCustomer.first_Name} ${draftCustomer.last_Name}`.trim() || draftCustomer.email || '')
    : '';

  // Latest values ref me rakhte hain taake setInterval hamesha taaza data dekhe
  latestDraftRef.current = {
    hasContent: hasDraftContent,
    payload: {
      customer_name: draftCustomerName,
      customer_reference: customerReference.trim(),
      items_count: orderedProducts.length,
      total_amount: Number(calculateTotal().toFixed(2)),
      data: draftData
    }
  };

  const waitForSave = async () => {
    let n = 0;
    while (savingRef.current && n < 50) {
      await new Promise((r) => setTimeout(r, 100));
      n++;
    }
  };

  const saveDraftNow = useCallback(async () => {
    if (skipAutosaveRef.current || savingRef.current) return;
    if (!selectedShopId) return;

    const { payload, hasContent } = latestDraftRef.current;
    if (!payload || !hasContent) return;

    const snapshot = JSON.stringify(payload.data);
    if (snapshot === lastSavedRef.current) return;

    savingRef.current = true;
    setSaveStatus('saving');
    try {
      if (draftIdRef.current) {
        await api.patch(`/order/drafts/${draftIdRef.current}/`, payload);
      } else {
        const res = await api.post('/order/drafts/', { ...payload, shop_id: parseInt(selectedShopId) });
        const newId = res.data?.id ?? res.data?.draft?.id;
        if (newId) {
          draftIdRef.current = newId;
          setDraftId(newId);
        }
      }
      lastSavedRef.current = snapshot;
      setSaveStatus('saved');
      setLastSavedAt(new Date());
    } catch (error) {
      // Draft kahin aur se delete ho gaya ho to agli baar naya bana do
      if (error?.response?.status === 404) {
        draftIdRef.current = null;
        setDraftId(null);
        lastSavedRef.current = '';
      }
      setSaveStatus('error');
    } finally {
      savingRef.current = false;
    }
  }, [selectedShopId]);

  useEffect(() => {
    const id = setInterval(saveDraftNow, 3000);
    return () => clearInterval(id);
  }, [saveDraftNow]);

  const fetchDrafts = useCallback(async () => {
    if (!selectedShopId) return;
    setDraftsLoading(true);
    try {
      const res = await api.get(`/order/drafts/?shop_id=${selectedShopId}`);
      const list = Array.isArray(res.data)
        ? res.data
        : (res.data?.results || res.data?.drafts || res.data?.data || []);
      setDrafts(list);
    } catch (error) {
      setDrafts([]);
    } finally {
      setDraftsLoading(false);
    }
  }, [selectedShopId]);

  // Badge count + list taaza rakhne ke liye (shop change / naya draft bana)
  useEffect(() => { fetchDrafts(); }, [fetchDrafts, draftId]);

  const openDrafts = async () => {
    await waitForSave();
    await saveDraftNow(); // jo abhi type kiya wo bhi save ho jaye
    await fetchDrafts();
    setView('drafts');
  };

  const removeCurrentDraftFromServer = async () => {
    await waitForSave();
    const id = draftIdRef.current;
    draftIdRef.current = null;
    lastSavedRef.current = '';
    setDraftId(null);
    setSaveStatus('');
    setLastSavedAt(null);
    if (id) {
      try { await api.delete(`/order/drafts/${id}/`); }
      catch (error) { console.error('Failed to delete draft:', error); }
    }
  };

  const startNewOrder = async () => {
    await waitForSave();
    await saveDraftNow(); // purana kaam apne draft me save rahe
    skipAutosaveRef.current = true;
    draftIdRef.current = null;
    lastSavedRef.current = '';
    setDraftId(null);
    setSaveStatus('');
    setLastSavedAt(null);
    resetOrderForm(false);
    setView('form');
    setTimeout(() => { skipAutosaveRef.current = false; }, 800);
  };

  const continueDraft = async (draft) => {
    await waitForSave();
    await saveDraftNow(); // current form ko bhi save kar do, phir draft kholo
    skipAutosaveRef.current = true;

    const d = draft.data || {};
    setOrderDetails({ ...freshOrderDetails(), ...(d.orderDetails || {}) });
    setAddressMode(d.addressMode || 'select');
    setManualAddress(d.manualAddress || '');
    setOrderedProducts(Array.isArray(d.orderedProducts) ? d.orderedProducts : []);
    setActiveRowId(null);
    setSelectedCity(d.selectedCity || 'all');
    setApplyDeliveryCharges(!!d.applyDeliveryCharges);
    setApplyVAT(!!d.applyVAT);
    setDeliveryCharges(Number(d.deliveryCharges) || 0);
    setDiscount(Number(d.discount) || 0);
    setCustomerReference(d.customerReference || '');
    setDeliveryMethod(d.deliveryMethod || 'Same Day');
    setCourierService(d.courierService || '');
    setCourierSearchText(d.courierService || '');
    setCourierServiceMode(d.courierServiceMode || 'select');
    setManualCourierService(d.manualCourierService || '');
    setAgentNameMode(d.agentNameMode || 'auto');
    setManualAgentName(d.manualAgentName || '');
    setAgentNumberMode(d.agentNumberMode || 'auto');
    setManualAgentNumber(d.manualAgentNumber || '');
    setNote(d.note || '');

    setCurrentProduct(EMPTY_PRODUCT);
    clearProductFilterInputs();
    setSearchResults([]); setShowSearchDropdown(false); setIsProductSelected(false);

    draftIdRef.current = draft.id;
    setDraftId(draft.id);
    lastSavedRef.current = JSON.stringify(d);
    setSaveStatus('saved');
    setLastSavedAt(draft.updated_at ? new Date(draft.updated_at) : new Date());
    setView('form');

    setTimeout(() => { skipAutosaveRef.current = false; }, 800);
  };

  const deleteDraft = async (draft) => {
    const result = await showConfirmAlert('Delete Draft', 'This draft will be permanently deleted. Continue?', 'Yes, Delete', 'Cancel');
    if (!result.isConfirmed) return;

    const isCurrent = String(draft.id) === String(draftIdRef.current);
    try {
      if (isCurrent) {
        skipAutosaveRef.current = true;
        await removeCurrentDraftFromServer();
        resetOrderForm(false);
        setTimeout(() => { skipAutosaveRef.current = false; }, 800);
      } else {
        await api.delete(`/order/drafts/${draft.id}/`);
      }
      fetchDrafts();
    } catch (error) {
      showErrorAlert('Delete Failed', 'Could not delete this draft. Please try again.');
    }
  };

  const handleSubmit = async () => {
    if (!orderDetails.customer) { showErrorAlert('Missing Required Fields', 'Please select a customer.'); return; }


    if (!orderDetails.phoneNumber) { showErrorAlert('Missing Required Fields', 'Please enter a phone number.'); return; }
    if (addressMode === 'select' && !orderDetails.address) { showErrorAlert('Missing Required Fields', 'Please select an address or type manually.'); return; }
    if (addressMode === 'manual' && !manualAddress.trim()) { showErrorAlert('Missing Required Fields', 'Please enter a manual address.'); return; }
    if (orderedProducts.length === 0) { showWarningAlert('No Products Added', 'Please add at least one product to the order.'); return; }

    if (orderedProducts.some(p => (parseInt(p.quantity) || 0) < 1)) {
      showWarningAlert('Invalid Quantity', 'Please remove or fix products with quantity 0.');
      return;
    }

    if (!selectedShopId) { showWarningAlert('Shop Required', 'Please select a specific shop to place the order.'); return; }

    if (isReferenceDuplicate()) {
      showErrorAlert('Duplicate Reference', 'This customer reference already exists for this customer. Please use a different one.');
      return;
    }

    const result = await showConfirmAlert('Confirm Order', 'Are you sure you want to place this order?', 'Yes, Place Order', 'Cancel');
    if (!result.isConfirmed) return;

    // Order place hotay waqt auto-save band, aur chalti hui save ka intezar
    skipAutosaveRef.current = true;
    await waitForSave();

    setSubmitting(true);
    showLoadingAlert('Placing Order...', 'Please wait while we process your order');

    try {
      const finalCourierService = courierServiceMode === 'select' ? courierService : manualCourierService;
      const finalCity = selectedCity && selectedCity !== 'all' ? selectedCity : null;
      const finalAgentName = agentNameMode === 'auto' ? agentName : manualAgentName;
      const finalAgentNumber = agentNumberMode === 'auto' ? agentNumber : manualAgentNumber;

      const cleanManualAddress = manualAddress.trim().slice(0, 255);

      const orderData = {
        customer: parseInt(orderDetails.customer),
        phone_Number: orderDetails.phoneNumber,
        address: addressMode === 'select' ? parseInt(orderDetails.address) : cleanManualAddress,
        address_is_manual: addressMode === 'manual',
        order_Status: orderDetails.orderStatus,
        order_Placment_Date: orderDetails.orderPlacementDate,
        order_Delivery_Time: orderDetails.orderDeliveryTime,
        order_Delivery_Date: orderDetails.orderDeliveryDate || getLocalDateString(),
        total_Amount: calculateTotal(),
        discount: discount,
        subtotal: calculateSubtotal(),
        delivery_Charges: applyDeliveryCharges ? deliveryCharges : 0,
        vat_amount: calculateVAT(),
        apply_vat: applyVAT,
        apply_delivery_charges: applyDeliveryCharges,
        customer_Reference: customerReference.trim(),
        delivery_Method: deliveryMethod,
        courier_service: finalCourierService,
        agent_Name: finalAgentName,
        agent_number: finalAgentNumber,
        note: note || '',
        shop_id: parseInt(selectedShopId),
        city: finalCity,
        products: orderedProducts.map(p => ({
          product_Id: p.product_Id,
          quantity: p.quantity,
          city: p.city || finalCity || ''
        }))
      };

      const response = await api.post('/order/placeOrder/', orderData);
      Swal.close();

      if (response.status === 201) {
        // ✅ Naya courier naam next time ke liye dropdown me save
        saveCourierIfNew(finalCourierService);

        // ✅ Order place ho gaya -> draft automatically delete
        await removeCurrentDraftFromServer();

        await showSuccessAlert('Order Placed Successfully!', `Order has been placed successfully for ${selectedShopName}.`);
        resetOrderForm(false);

        // Refetch references so the newly added one is registered
        try {
          const refResponse = await api.get(`/order/getAllorder/?shop_id=${selectedShopId}`);
          let orders = refResponse.data.orders || refResponse.data || [];
          const refs = orders
            .filter(o => o.customer && o.customer_Reference && o.customer_Reference.trim() !== '')
            .map(o => ({ customerId: o.customer.id, reference: o.customer_Reference.trim() }));
          setCustomerReferences(refs);
        } catch (e) {}

        fetchDrafts();
      }
    } catch (error) {
      Swal.close();
      showErrorAlert('Order Failed', extractServerErrorMessage(error));
    } finally {
      setSubmitting(false);
      setTimeout(() => { skipAutosaveRef.current = false; }, 1500);
    }
  };

  // =====================================================================
  // DRAFT UI HELPERS
  // =====================================================================
  const headerFlexStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' };
  const draftBtnStyle = {
    display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#fff', color: '#004aad',
    border: '1px solid #004aad', borderRadius: '6px', padding: '8px 14px', fontSize: '0.9rem',
    fontWeight: 600, cursor: 'pointer'
  };
  const primaryBtnStyle = { ...draftBtnStyle, background: '#004aad', color: '#fff' };
  const badgeStyle = {
    background: '#dc3545', color: '#fff', borderRadius: '10px', padding: '1px 8px',
    fontSize: '0.72rem', fontWeight: 700, lineHeight: '1.4'
  };

  const saveStatusText =
    saveStatus === 'saving' ? 'Saving draft…'
    : saveStatus === 'saved' ? `Draft saved${lastSavedAt ? ' ' + lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}`
    : saveStatus === 'error' ? 'Draft not saved'
    : '';

  // ---------------------------------------------------------------------
  // DRAFTS PAGE
  // ---------------------------------------------------------------------
  if (view === 'drafts') {
    return (
      <div className="place-order-page-wrapper">
        <div className="place-order-container">
          <div className="place-order-content">
            <div className="order-section-box">
              <div className="section-box-header" style={headerFlexStyle}>
                <div className="order-title-wrapper">
                  <FaRegFileAlt className="order-title-icon" />
                  <h2 className="order-title">Drafts{selectedShopName ? ` — ${selectedShopName}` : ''}</h2>
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button type="button" style={primaryBtnStyle} onClick={startNewOrder}>
                    <FaPlus /> New Order
                  </button>
                  <button type="button" style={draftBtnStyle} onClick={() => setView('form')}>
                    <FaArrowLeft /> Back to order
                  </button>
                </div>
              </div>

              {draftsLoading ? (
                <p style={{ padding: '1.5rem', color: '#666' }}>Loading drafts...</p>
              ) : drafts.length === 0 ? (
                <p style={{ padding: '1.5rem', color: '#666' }}>
                  No drafts yet.
                </p>
              ) : (
                <div className="products-table-container">
                  <table className="products-table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>Customer</th>
                        <th style={{ textAlign: 'left' }}>Reference</th>
                        <th style={{ textAlign: 'center' }}>Items</th>
                        <th style={{ textAlign: 'right' }}>Total{displayCurrencySymbol ? ` (${displayCurrencySymbol})` : ''}</th>
                        <th style={{ textAlign: 'left' }}>Last saved</th>
                        <th style={{ textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drafts.map((d) => {
                        const isCurrent = String(d.id) === String(draftId);
                        return (
                          <tr key={d.id} style={isCurrent ? { background: '#f0f7ff' } : undefined}>
                            <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {d.customer_name || <span style={{ color: '#999', fontStyle: 'italic' }}>No customer</span>}
                              {isCurrent && (
                                <span style={{ marginLeft: '8px', fontSize: '0.72rem', color: '#004aad', fontWeight: 700 }}>(open now)</span>
                              )}
                            </td>
                            <td>{d.customer_reference || '—'}</td>
                            <td style={{ textAlign: 'center' }}>{d.items_count ?? 0}</td>
                            <td style={{ textAlign: 'right' }}>{formatMoney(d.total_amount)}</td>
                            <td>{d.updated_at ? new Date(d.updated_at).toLocaleString() : '—'}</td>
                            <td style={{ textAlign: 'center' }}>
                              <button type="button" className="remove-btn" style={{ background: '#004aad', color: '#fff', marginRight: '6px' }} onClick={() => continueDraft(d)}>
                                {isCurrent ? 'Open' : 'Continue'}
                              </button>
                              <button type="button" className="remove-btn" onClick={() => deleteDraft(d)}>Delete</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="place-order-page-wrapper">
      <div className="place-order-container">
        <div className="place-order-content">

          {/* ============================================
              SECTION 1: ORDER DETAILS BOX
              ============================================ */}
          <div className="order-section-box">
            <div className="section-box-header" style={headerFlexStyle}>
              <div className="order-title-wrapper">
                <IoMdCart className="order-title-icon" />
                <h2 className="order-title">Place Orders</h2>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                {saveStatusText && (
                  <span style={{ fontSize: '0.78rem', color: saveStatus === 'error' ? '#dc3545' : '#6c757d' }}>
                    {saveStatusText}
                  </span>
                )}
                <button type="button" style={draftBtnStyle} onClick={openDrafts}>
                  <FaRegFileAlt /> Drafts
                  {drafts.length > 0 && <span style={badgeStyle}>{drafts.length}</span>}
                </button>
              </div>
            </div>

            <div className="order-fields-3col">
              <div className="order-field">
                <label className="order-label">Order Tracking Id:</label>
                <div className="order-value-box">
                  <input type="text" className="order-input" value={orderDetails.orderTrackingId} onChange={(e) => handleInputChange('orderTrackingId', e.target.value)} placeholder="Auto-generated" />
                </div>
              </div>

              <div className="order-field">
                <label className="order-label">Order Status:</label>
                <div className="order-value-box">
                  <select className="order-select" value={orderDetails.orderStatus} onChange={(e) => handleInputChange('orderStatus', e.target.value)}>
                    <option value="Pending">Pending</option>
                    <option value="Processing">Processing</option>
                    <option value="Shipped">Shipped</option>
                    <option value="Delivered">Delivered</option>
                    <option value="Canceled">Canceled</option>
                    <option value="Returned">Returned</option>
                  </select>
                </div>
              </div>

              <div className="order-field">
                <label className="order-label">Order Placement Date:</label>
                <div className="order-value-box">
                  <input type="date" className="order-input" value={orderDetails.orderPlacementDate} onChange={(e) => handleOrderPlacementDateChange(e.target.value)} />
                </div>
              </div>

              <div className="order-field">
                <label className="order-label">Order Delivery Date:</label>
                <div className="order-value-box">
                  <input type="date" className="order-input" value={orderDetails.orderDeliveryDate} onChange={(e) => handleOrderDeliveryDateChange(e.target.value)} />
                </div>
              </div>

              <div className="order-field">
                <label className="order-label">Order Delivery Time:</label>
                <div className="order-value-box">
                  <input type="time" className="order-input" value={orderDetails.orderDeliveryTime} onChange={(e) => handleInputChange('orderDeliveryTime', e.target.value)} step="1" />
                </div>
              </div>

              <div className="order-field">
                <label className="order-label">City:</label>
                <div className="order-value-box">
                  <select className="order-select" value={selectedCity} onChange={handleCityChange}>
                    <option value="all">All Cities</option>
                    {(() => {
                      const selectedCustomer = orderDetails.customer
                        ? customers.find(c => String(c.id || c.user_id || c.customer_id) === String(orderDetails.customer))
                        : null;
                      const customerCity = selectedCustomer?.selected_city || '';
                      const cityList = [...cities];
                      if (customerCity && !cityList.includes(customerCity)) cityList.unshift(customerCity);
                      return cityList.length > 0 ? (
                        cityList.map((city, index) => (<option key={`${city}-${index}`} value={city}>{city}</option>))
                      ) : (<option value="" disabled>No cities available</option>);
                    })()}
                  </select>
                </div>
              </div>

              <div className="order-field">
                <label className="order-label">Customer:</label>
                <div className="order-value-box">
                  <select className="order-select" value={orderDetails.customer} onChange={(e) => handleCustomerChange(e.target.value)}>
                    <option value="">Select a customer</option>
                    {customers.length > 0 ? (
                      customers.map((customer) => (
                        <option key={customer.id || customer.user_id || customer.customer_id} value={customer.id || customer.user_id || customer.customer_id}>
                          {getCustomerDisplayName(customer)}
                        </option>
                      ))
                    ) : (<option value="" disabled>No customers available</option>)}
                  </select>
                </div>
              </div>

              <div className="order-field">
                <label className="order-label">Phone Number:</label>
                <div className="order-value-box">
                  <input type="tel" className="order-input" placeholder="Enter phone number" value={orderDetails.phoneNumber} onChange={(e) => handleInputChange('phoneNumber', e.target.value)} />
                </div>
              </div>

              <div className="order-field">
                <label className="order-label">Customer Reference:</label>
                <div className="order-value-box" style={isReferenceDuplicate() ? { borderColor: '#f5c2c7', backgroundColor: '#fff3cd' } : {}}>
                  <input
                    type="text"
                    className="order-input"
                    value={customerReference}
                    onChange={(e) => setCustomerReference(e.target.value)}
                    placeholder="Enter customer reference"
                    style={{ backgroundColor: isReferenceDuplicate() ? '#fff3cd' : 'transparent' }}
                  />
                </div>

                {isReferenceDuplicate() && (
                  <div style={{
                    marginTop: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#d39e00',
                    fontSize: '0.8rem',
                    fontWeight: '500'
                  }}>
                    <FaExclamationTriangle style={{ marginRight: '6px', fontSize: '0.85rem' }} />
                    This customer reference already exists in the database. Please use a different one.
                  </div>
                )}
              </div>

              <div className="order-field order-field-span-3">
                <label className="order-label">Address:</label>
                <div className="address-mode-container">
                  {addressMode === 'select' ? (
                    <div className="address-select-wrapper">
                      <div className="order-value-box address-select-box">
                        <select className="order-select" value={orderDetails.address} onChange={(e) => handleInputChange('address', e.target.value)}>
                          <option value="">{orderDetails.customer ? 'Select an address for this customer' : 'Select an address'}</option>
                          {getCustomerAddresses().length > 0 ? (
                            getCustomerAddresses().map((address) => (
                              <option key={address.address_Id || address.id} value={address.address_Id || address.id}>
                                {getAddressDisplay(address)}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>{orderDetails.customer ? 'No addresses found for this customer' : 'No addresses available'}</option>
                          )}
                        </select>
                      </div>
                      <button type="button" className="address-toggle-btn" onClick={switchToManual}>
                        <FaPencilAlt style={{ marginRight: '5px' }} /> Type manually
                      </button>
                    </div>
                  ) : (
                    <div className="address-select-wrapper">
                      <div className="order-value-box address-select-box">
                        <input
                          type="text"
                          className="order-input"
                          placeholder="Type address manually..."
                          value={manualAddress}
                          maxLength={255}
                          onChange={(e) => setManualAddress(e.target.value)}
                        />
                      </div>
                      <button type="button" className="address-toggle-btn" onClick={switchToSelect}>
                        <FaList style={{ marginRight: '5px' }} /> Select from list
                      </button>
                    </div>
                  )}
                  <span className="order-hint">
                    {addressMode === 'manual'
                      ? `${manualAddress.length}/255 characters`
                      : orderDetails.customer
                        ? (orderDetails.address ? '✓ Address auto-selected from customer profile.' : 'No valid address found for this customer.')
                        : 'Select a customer to auto-fill their address'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ============================================
              SECTION 2: ORDERED PRODUCTS BOX
              ============================================ */}
          <div className="products-section-box">
            <div className="section-header-reset">
              <h3 className="section-title">Add ordered Product</h3>
              <div className="header-actions">
                <button className="reset-filters-btn" onClick={resetFilters}>Reset Filters</button>
              </div>
            </div>

            <div className="product-filters-row">
              <div className="filter-group">
                <label>Type:</label>
                <div className="searchable-filter-container">
                  <input type="text" className="filter-select searchable-filter-input" placeholder="Search or type type..." value={typeSearchText} onChange={(e) => handleTypeSearchChange(e.target.value)} onClick={handleTypeClick} />
                  {showTypeDropdown && filteredTypes.length > 0 && (
                    <div className="searchable-dropdown">
                      {filteredTypes.map((type) => (<div key={type.type_id} className="searchable-dropdown-item" onClick={() => selectTypeOption(type)}>{type.type_name}</div>))}
                    </div>
                  )}
                  {showTypeDropdown && filteredTypes.length === 0 && typeSearchText && (
                    <div className="searchable-dropdown"><div className="searchable-dropdown-item custom-value"><FaPencilAlt style={{ marginRight: '5px' }} />Custom value: {typeSearchText}</div></div>
                  )}
                </div>
              </div>

              <div className="filter-group">
                <label>Category:</label>
                <div className="searchable-filter-container">
                  <input type="text" className="filter-select searchable-filter-input" placeholder="Search or type category..." value={categorySearchText} onChange={(e) => handleCategorySearchChange(e.target.value)} onClick={handleCategoryClick} />
                  {showCategoryDropdown && filteredCategories.length > 0 && (
                    <div className="searchable-dropdown">
                      {filteredCategories.map((category) => (<div key={category.catrgort_id} className="searchable-dropdown-item" onClick={() => selectCategoryOption(category)}>{category.category_Name}</div>))}
                    </div>
                  )}
                  {showCategoryDropdown && filteredCategories.length === 0 && categorySearchText && (
                    <div className="searchable-dropdown"><div className="searchable-dropdown-item custom-value"><FaPencilAlt style={{ marginRight: '5px' }} />Custom value: {categorySearchText}</div></div>
                  )}
                </div>
              </div>

              <div className="filter-group">
                <label>Variation:</label>
                <div className="searchable-filter-container">
                  <input type="text" className="filter-select searchable-filter-input" placeholder="Search or type variation..." value={variationSearchText} onChange={(e) => handleVariationSearchChange(e.target.value)} onClick={handleVariationClick} />
                  {showVariationDropdown && filteredVariations.length > 0 && (
                    <div className="searchable-dropdown">
                      {filteredVariations.map((variation) => (<div key={variation.variation_id} className="searchable-dropdown-item" onClick={() => selectVariationOption(variation)}>{variation.variation_name}</div>))}
                    </div>
                  )}
                  {showVariationDropdown && filteredVariations.length === 0 && variationSearchText && (
                    <div className="searchable-dropdown"><div className="searchable-dropdown-item custom-value"><FaPencilAlt style={{ marginRight: '5px' }} />Custom value: {variationSearchText}</div></div>
                  )}
                  {showVariationDropdown && filteredVariations.length === 0 && !variationSearchText && (currentProduct.selectCategory || customCategoryValue) && (
                    <div className="searchable-dropdown"><div className="searchable-dropdown-item custom-value">No variations found for this category</div></div>
                  )}
                </div>
              </div>

              <div className="filter-group">
                <label>Implant Company:</label>
                <div className="searchable-filter-container">
                  <input type="text" className="filter-select searchable-filter-input" placeholder="Search or type company..." value={companySearchText} onChange={(e) => handleCompanySearchChange(e.target.value)} onClick={handleCompanyClick} />
                  {showCompanyDropdown && filteredCompanies.length > 0 && (
                    <div className="searchable-dropdown">
                      {filteredCompanies.map((company) => (<div key={company.company_id} className="searchable-dropdown-item" onClick={() => selectCompanyOption(company)}>{company.company_name}</div>))}
                    </div>
                  )}
                  {showCompanyDropdown && filteredCompanies.length === 0 && companySearchText && (
                    <div className="searchable-dropdown"><div className="searchable-dropdown-item custom-value"><FaPencilAlt style={{ marginRight: '5px' }} />Custom value: {companySearchText}</div></div>
                  )}
                </div>
              </div>

              <div className="filter-group">
                <label>Implant Connection:</label>
                <div className="searchable-filter-container">
                  <input type="text" className="filter-select searchable-filter-input" placeholder="Search or type connection..." value={connectionSearchText} onChange={(e) => handleConnectionSearchChange(e.target.value)} onClick={handleConnectionClick} />
                  {showConnectionDropdown && filteredConnections.length > 0 && (
                    <div className="searchable-dropdown">
                      {filteredConnections.map((brand) => (<div key={brand.brand_id} className="searchable-dropdown-item" onClick={() => selectConnectionOption(brand)}>{brand.brand_name}</div>))}
                    </div>
                  )}
                  {showConnectionDropdown && filteredConnections.length === 0 && connectionSearchText && (
                    <div className="searchable-dropdown"><div className="searchable-dropdown-item custom-value"><FaPencilAlt style={{ marginRight: '5px' }} />Custom value: {connectionSearchText}</div></div>
                  )}
                  {showConnectionDropdown && filteredConnections.length === 0 && !connectionSearchText && (currentProduct.selectImplantCompany || customCompanyValue) && (
                    <div className="searchable-dropdown"><div className="searchable-dropdown-item custom-value">No connections found for this company</div></div>
                  )}
                </div>
              </div>
            </div>

            <div className="product-detail-row">
              <div className="product-search-group">
                <label>Product:</label>
                <div className="search-container" ref={searchContainerRef}>
                  <input
                    type="text"
                    className="product-search-input"
                    placeholder={filtersReady ? 'Search product by SKU or name...' : 'Select Category and Variation first'}
                    disabled={!filtersReady}
                    value={currentProduct.productSearch}
                    onChange={(e) => {
                      handleProductChange('productSearch', e.target.value);
                      setIsProductSelected(false);
                      setActiveRowId(null);
                      setCurrentProduct(prev => ({
                        ...prev, productId: null, productPrice: '', cityStock: [],
                        selectedCityStock: null, availableStock: null, total: 0
                      }));
                    }}
                    onFocus={() => filtersReady && searchResults.length > 0 && setShowSearchDropdown(true)}
                  />
                  {filtersReady && searchLoading && <span className="search-loading">Searching...</span>}
                  {filtersReady && showSearchDropdown && !searchLoading && searchResults.length === 0 && !isProductSelected && (hasActiveFilter || currentProduct.productSearch) && (
                    <div className="search-dropdown">
                      <div className="search-item"><span className="product-name">No products match the selected filters</span></div>
                    </div>
                  )}
                  {filtersReady && showSearchDropdown && searchResults.length > 0 && (
                    <div className="search-dropdown">
                      {searchResults.map((product) => (
                        <div key={product.product_Id} className="search-item" onClick={() => selectProduct(product)}>
                          {product.product_Name} ({product.SKU})
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {!filtersReady && (
                  <small style={{ color: '#999', display: 'block', marginTop: '4px' }}>
                    First select Category and Variation, then the products will appear here
                  </small>
                )}
              </div>
            </div>

            {/* ✅ 3-COLUMN GRID: City & Stock | Quantity | Product Price */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem', width: '100%' }}>

              {/* COLUMN 1: Select your city */}
              <div className="city-stock-wrapper city-stock-container" style={{ background: '#ffffff', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '1rem', transition: 'all 0.2s ease', position: 'relative' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem', display: 'block' }}>
                  SELECT YOUR CITY <span style={{color: '#dc3545'}}>*</span>
                </label>

                {currentProduct.productId && currentProduct.cityStock.length > 0 ? (
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      className="order-input"
                      value={currentProduct.selectedCityStock ? `${currentProduct.selectedCityStock.city}: ${currentProduct.selectedCityStock.stock} units` : 'Select City'}
                      onClick={() => setShowCityStockDropdown(!showCityStockDropdown)}
                      readOnly
                      style={{ width: '100%', border: '1px solid #e0e0e0', padding: '0.6rem', paddingRight: '2.2rem', borderRadius: '4px', background: '#fff', fontSize: '0.9rem', cursor: 'pointer' }}
                    />
                    <FaChevronDown
                      style={{
                        position: 'absolute', right: '12px', top: '50%',
                        transform: `translateY(-50%) rotate(${showCityStockDropdown ? 180 : 0}deg)`,
                        transition: 'transform 0.2s ease', pointerEvents: 'none', color: '#666', fontSize: '0.8rem'
                      }}
                    />
                    {showCityStockDropdown && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e0e0e0', borderRadius: '4px', zIndex: 1000, maxHeight: '150px', overflowY: 'auto', marginTop: '2px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                        {currentProduct.cityStock.map((item, index) => (
                          <div
                            key={index}
                            onClick={() => handleCityStockSelect(item)}
                            style={{ padding: '0.7rem 1rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', fontSize: '0.9rem', color: '#333' }}
                            onMouseEnter={(e) => e.target.style.background = '#f0f7ff'}
                            onMouseLeave={(e) => e.target.style.background = '#fff'}
                          >
                            {item.city}: {item.stock} units
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ color: '#999', fontSize: '0.9rem', fontStyle: 'italic', paddingTop: '0.5rem' }}>
                    Select a product to view cities & stock
                  </div>
                )}
              </div>

              {/* COLUMN 2: Quantity */}
              <div className="quantity-group" style={{ background: '#ffffff', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '1rem', transition: 'all 0.2s ease', display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>QUANTITY:</label>
                <input
                  type="number"
                  className="quantity-input"
                  value={currentProduct.quantity}
                  onChange={(e) => handleQuantityChange(e.target.value)}
                  min="0"
                  max={currentProduct.selectedCityStock ? currentProduct.selectedCityStock.stock : (currentProduct.availableStock !== null && currentProduct.availableStock !== undefined ? currentProduct.availableStock : undefined)}
                  style={{ width: '100%', padding: '0.6rem', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '0.9rem' }}
                />
              </div>

              {/* COLUMN 3: Product Price */}
              <div className="price-group" style={{ background: '#ffffff', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '1rem', transition: 'all 0.2s ease', display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>PRODUCT PRICE{displayCurrencySymbol ? ` (${displayCurrencySymbol})` : ''}:</label>
                <input
                  type="number"
                  className="price-input"
                  placeholder="Product price"
                  value={currentProduct.productPrice}
                  readOnly
                  style={{ width: '100%', padding: '0.6rem', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '0.9rem', background: '#f9f9f9' }}
                />
              </div>
            </div>

            <div className="add-product-btn-container">
              <button className="add-another-product-btn" onClick={addProduct}>+ Add another product</button>
            </div>

            {orderedProducts.length > 0 && (
              <div className="products-table-container">
                <table className="products-table">
                  <thead>
                    <tr>
                      <th style={{ minWidth: '300px', width: '45%' }}>Product Name</th>
                      <th style={{ width: '10%', textAlign: 'center' }}>Qty</th>
                      <th style={{ width: '15%', textAlign: 'right' }}>Price{displayCurrencySymbol ? ` (${displayCurrencySymbol})` : ''}</th>
                      <th style={{ width: '15%', textAlign: 'right' }}>Total{displayCurrencySymbol ? ` (${displayCurrencySymbol})` : ''}</th>
                      <th style={{ width: '15%', textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderedProducts.map((product) => (
                      <tr key={product.id}>
                        <td style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.4' }}>{product.productName}</td>
                        <td style={{ textAlign: 'center' }}>{product.quantity}</td>
                        <td style={{ textAlign: 'right' }}>{formatMoney(product.productPrice)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600, color: '#004aad' }}>{formatMoney(product.total)}</td>
                        <td style={{ textAlign: 'center' }}><button className="remove-btn" onClick={() => removeProduct(product.id)}>Remove</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="order-totals-section">
              <div className="total-item-row"><div className="total-item"><span className="total-label">Total Amount:</span><span className="total-value">{formatMoney(calculateSubtotal())}</span></div></div>
              <div className="total-item-row"><div className="total-item"><span className="total-label">Delivery Charges:</span><input type="number" className="delivery-input" value={deliveryCharges} onChange={(e) => setDeliveryCharges(parseFloat(e.target.value) || 0)} disabled={!applyDeliveryCharges} placeholder="0" /></div></div>
              <div className="total-item-row"><div className="total-item"><span className="total-label">Discount:</span><input type="number" className="discount-input" value={discount} onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)} placeholder="0" /></div></div>
              <div className="checkbox-row">
                <div className="checkbox-group"><label><input type="checkbox" checked={applyDeliveryCharges} onChange={(e) => setApplyDeliveryCharges(e.target.checked)} />Apply Delivery Charges</label></div>
                <div className="checkbox-group"><label><input type="checkbox" checked={applyVAT} onChange={(e) => setApplyVAT(e.target.checked)} />Apply VAT (20%)</label></div>
              </div>
              <div className="subtotal-row"><span className="subtotal-label">Subtotal:</span><span className="subtotal-value">{formatMoney(calculateTotal())}</span></div>
            </div>
          </div>

          {/* ============================================
              SECTION 3: ADDITIONAL FIELDS BOX + PLACE ORDER BUTTON
              ============================================ */}
          <div className="additional-fields-box">
            <div className="additional-fields-grid">
              <div className="field-group-col">
                <label className="field-label">Courier Service:</label>
                <div className="courier-mode-container">
                  {courierServiceMode === 'select' ? (
                    <div className="courier-select-wrapper">
                      <div className="order-value-box courier-select-box">
                        <div className="courier-search-container">
                          <input
                            type="text"
                            className="order-input courier-search-input"
                            placeholder="Search or select courier..."
                            value={courierSearchText}
                            onChange={(e) => handleCourierSearchChange(e.target.value)}
                            onClick={handleCourierServiceClick}
                          />

                          <FaChevronDown className="courier-chevron-icon" />
                          {showCourierDropdown && (
                            <div className="searchable-dropdown courier-dropdown">
                              {filteredCourierServices.length > 0 ? (
                                filteredCourierServices.map((service) => {
                                  const isSaved = savedCouriers.includes(service);
                                  return (
                                    <div
                                      key={service}
                                      className="searchable-dropdown-item"
                                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                      onClick={() => selectCourierOption(service)}
                                    >
                                      <span>{service}</span>
                                      {isSaved && (
                                        <span
                                          title="Remove from list"
                                          onClick={(e) => { e.stopPropagation(); removeSavedCourier(service); }}
                                          style={{ color: '#dc3545', fontWeight: 700, padding: '0 6px', cursor: 'pointer' }}
                                        >
                                          ×
                                        </span>
                                      )}
                                    </div>
                                  );
                                })
                              ) : (
                                <div
                                  className="searchable-dropdown-item custom-value"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    const clean = courierSearchText.trim();
                                    if (!clean) return;
                                    selectCourierOption(clean);
                                    saveCourierIfNew(clean);
                                  }}
                                >
                                  <FaPencilAlt style={{ marginRight: '5px' }} />Add "{courierSearchText.trim()}" as new courier
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <button type="button" className="address-toggle-btn" onClick={switchToManualCourier}><FaPencilAlt /></button>
                    </div>
                  ) : (
                    <div className="courier-select-wrapper">
                      <div className="order-value-box courier-select-box">
                        <input type="text" className="order-input" placeholder="Type courier manually..." value={manualCourierService} onChange={(e) => setManualCourierService(e.target.value)} />
                      </div>
                      <button type="button" className="address-toggle-btn" onClick={switchToSelectCourier}><FaList /></button>
                    </div>
                  )}
                </div>
              </div>

              <div className="field-group-col">
                <label className="field-label">Agent Name:</label>
                <div className="address-mode-container">
                  {agentNameMode === 'auto' ? (
                    <div className="address-select-wrapper">
                      <div className="order-value-box address-select-box">
                        <input type="text" className="order-input" value={agentName} readOnly placeholder="Auto-filled" style={{ backgroundColor: '#f7f9fc', cursor: 'not-allowed' }} />
                      </div>
                      <button type="button" className="address-toggle-btn" onClick={switchAgentNameToManual}><FaPencilAlt /></button>
                    </div>
                  ) : (
                    <div className="address-select-wrapper">
                      <div className="order-value-box address-select-box">
                        <input type="text" className="order-input" value={manualAgentName} onChange={(e) => setManualAgentName(e.target.value)} placeholder="Type agent name..." />
                      </div>
                      <button type="button" className="address-toggle-btn" onClick={switchAgentNameToAuto}><FaList /></button>
                    </div>
                  )}
                </div>
              </div>

              <div className="field-group-col">
                <label className="field-label">Agent Number:</label>
                <div className="address-mode-container">
                  {agentNumberMode === 'auto' ? (
                    <div className="address-select-wrapper">
                      <div className="order-value-box address-select-box">
                        <input type="tel" className="order-input" value={agentNumber} readOnly placeholder="Auto-filled" style={{ backgroundColor: '#f7f9fc', cursor: 'not-allowed' }} />
                      </div>
                      <button type="button" className="address-toggle-btn" onClick={switchAgentNumberToManual}><FaPencilAlt /></button>
                    </div>
                  ) : (
                    <div className="address-select-wrapper">
                      <div className="order-value-box address-select-box">
                        <input type="tel" className="order-input" value={manualAgentNumber} onChange={(e) => setManualAgentNumber(e.target.value)} placeholder="Type agent number..." />
                      </div>
                      <button type="button" className="address-toggle-btn" onClick={switchAgentNumberToAuto}><FaList /></button>
                    </div>
                  )}
                </div>
              </div>

              <div className="field-group-col">
                <label className="field-label">Delivery Day:</label>
                <select className="field-select" value={deliveryMethod} onChange={(e) => setDeliveryMethod(e.target.value)}>
                  <option value="Same Day">Same Day</option>
                  <option value="Next Day">Next Day</option>
                </select>
              </div>

              <div className="field-group-col field-group-span-2">
                <label className="field-label">Note (Optional):</label>
                <textarea className="field-textarea" rows="3" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Enter note here... (Optional)" />
                <small className="note-hint">This field is optional. You can leave it empty.</small>
              </div>
            </div>

            <div className="order-action-inside-box">
              <button className="submit-order-btn" onClick={handleSubmit} disabled={submitting || !selectedShopId}>
                {submitting ? 'Placing Order...' : 'Place Order'}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Placeorders;